import React, { useEffect, useState, useContext } from "react";
import { Modal, Checkbox, Input, Divider, Tag, Button } from "antd";
import _ from "lodash";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Users,
  ChevronRight,
  ThumbsUp,
  ThumbsDown,
  AlertTriangle,
} from "lucide-react";

import defaultLogo from "../../assets/images/default_company_logo.jpg";
import { getSubscriptionUserInfo, getUserInfo } from "../../utils/utils";
import { getCompanyLogoUrl } from "../../utils/getCompanyLogoUrl";
import TrialModal from "../../common/TrialModal";
import {
  submitErrorForm,
  postRelavantCompany,
  getRelavantCompany,
  resetPostRelavantCompany,
  updateRelavantCompany,
} from "../../actionCreator/companyDetailsActionCreator";
import { emailRegex } from "../../config";
import { AuthContext } from "../../context/AuthContext";

// SimilarCompany Component

const SimilarCompany = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);
  const [showModal, setShowModal] = useState(false);
  const [similarCount, setSimilarCount] = useState(5);
  const [openErrorForm, setOpenErrorForm] = useState(false);
  const [checkCompanyStatus, setCheckCompanyStatus] = useState(0);
  const formIntialValue = {
    telephone: { disabled: true, value: "", status: null },
    address: { disabled: true, value: "", status: null },
    city: { disabled: true, value: "", status: null },
    zip: { disabled: true, value: "", status: null },
    employee: { disabled: true, value: "", status: null },
    website: { disabled: true, value: "", status: null },
    name: { disabled: true, value: "", status: null },
    email: { disabled: true, value: "", status: null },
    comment: { disabled: true, value: "", status: null },
  };
  const { TextArea } = Input;
  const [errorForm, setErrorForm] = useState(formIntialValue);
  const companyDetails = useSelector(
    (state) => state.companyDetailsReducer.companyDetails,
  );
  const similarCompanyList = useSelector(
    (state) => state.companyDetailsReducer.similarCompanyList,
  );
  const getRelavantCompanyDetails = useSelector(
    (state) => state.companyDetailsReducer.getRelavantCompany,
  );

  const userAccountInfo = useSelector(
    (state) => state.CommonReducer.accountInfo,
  );

  useEffect(() => {
    const [data] = getRelavantCompanyDetails || [];
    if (data) {
      setCheckCompanyStatus(data?.prescribedby === "True" ? 1 : 2);
    }
  }, [getRelavantCompanyDetails]);

  useEffect(() => {
    dispatch(resetPostRelavantCompany());
    if (Object.keys(getUserInfo()).length && companyDetails?.id) {
      const { id } = getUserInfo();
      dispatch(getRelavantCompany(id, companyDetails?.id));
    }
  }, [userAccountInfo, companyDetails, dispatch]);

  useEffect(() => {
    setSimilarCount(
      similarCompanyList.length < 5 ? similarCompanyList.length : 5,
    );
  }, [similarCompanyList]);

  const getDetails = (id, name) => {
    let cname = name.replaceAll(" ", "-").toLowerCase();
    navigate(`/company-summary/${id}/${cname}`);
  };

  const checkLoginStatus = () => {
    let isLoggedIn = false;
    if (isAuthenticated) {
      setShowModal(false);
      isLoggedIn = true;
    } else {
      setShowModal(true);
      isLoggedIn = false;
    }
    return isLoggedIn;
  };

  const getMoreSimilarCount = () => {
    let isLoggedIn = checkLoginStatus();
    if (isLoggedIn) {
      if (similarCount === 5) {
        setSimilarCount(similarCompanyList.length);
      } else {
        setSimilarCount(5);
      }
    }
  };

  const checkRelavantCompany = (prescribedby, checkCompanyStatus) => {
    let isLoggedIn = checkLoginStatus();
    if (isLoggedIn) {
      const { id } = getUserInfo();
      const { id: accountId } = getSubscriptionUserInfo();
      const payload = {
        accountId: accountId,
        companyId: companyDetails?.id,
        iscompany: true,
        prescribedby: prescribedby === 1 ? true : false,
        userId: id,
      };

      if (checkCompanyStatus === 0) {
        dispatch(postRelavantCompany(payload));
        setCheckCompanyStatus(prescribedby === 1 ? 1 : 2);
      } else {
        dispatch(
          updateRelavantCompany(
            payload,
            getRelavantCompanyDetails[0]?.id,
          ),
        );
        setCheckCompanyStatus(prescribedby === 1 ? 1 : 2);
      }
    }
  };

  const handleErrorForm = () => {
    let isLoggedIn = checkLoginStatus();
    if (isLoggedIn) {
      setOpenErrorForm(true);
    }
  };

  const closeErrorForm = () => {
    setOpenErrorForm(false);
    setErrorForm(formIntialValue);
  };

  const enableField = (ele) => {
    setErrorForm({
      ...errorForm,
      [ele.target.name]: ele.target.checked
        ? { ...errorForm[ele.target.name], disabled: !ele.target.checked }
        : { value: "", disabled: !ele.target.checked },
    });
  };

  const onInputChange = (ele) => {
    setErrorForm({
      ...errorForm,
      [ele.target.name]: {
        ...errorForm[ele.target.name],
        value: ele.target.value,
        status: null,
      },
    });
  };

  const onSubmitForm = () => {
    const regEx = new RegExp(emailRegex);
    const clone = _.cloneDeep(errorForm);
    if (!errorForm?.name?.value) {
      clone.name.status = "error";
      setErrorForm(clone);
      return false;
    }

    if (!errorForm?.email?.value || !regEx.test(errorForm?.email?.value)) {
      clone.email.status = "error";
      setErrorForm(clone);
      return false;
    }

    let newPayload = {};
    Object.keys(errorForm).forEach((key) => {
      newPayload = {
        ...newPayload,
        [key]: errorForm[key].value,
      };
    });
    const { id } = getUserInfo();
    const { id: accountId } = getSubscriptionUserInfo();
    const payload = {
      accountId: accountId,
      companyId: companyDetails?.id,
      description: newPayload,
      iscompany: true,
      iscorrected: true,
      userId: id,
    };
    dispatch(submitErrorForm(payload));
    setOpenErrorForm(false);
    setErrorForm(formIntialValue);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const redirectToSignup = () => {
    setShowModal(false);
    navigate("/signup");
  };

  const renderSimilarCompanyList = () => {
    let similarList = [];
    for (let i = 0; i < similarCount; i++) {
      if (!similarCompanyList[i]) continue;
      similarList.push(
        <div
          key={`similarCompany_${i}`}
          className="tw-flex tw-group tw-gap-4 tw-p-3 hover:tw-bg-slate-50/50 tw-border tw-border-solid tw-border-transparent hover:tw-border-slate-100 tw-rounded-xl tw-transition-all tw-cursor-pointer"
          onClick={() => getDetails(similarCompanyList[i]?.id, similarCompanyList[i]?.name)}
        >
          <div className="tw-bg-secondary-color tw-size-12 tw-rounded-xl tw-overflow-hidden tw-border tw-border-solid tw-border-slate-200/60 tw-flex tw-items-center tw-justify-center tw-shrink-0">
            {similarCompanyList[i]?.companyLogoUrl ? (
              <img
                src={getCompanyLogoUrl(similarCompanyList[i]?.companyLogoUrl)}
                alt=""
                className="tw-w-full tw-h-full tw-object-contain"
                onError={(e) => {
                  e.currentTarget.src = defaultLogo;
                }}
              />
            ) : (
              <img src={defaultLogo} alt="" className="tw-w-full tw-h-full tw-object-contain" />
            )}
          </div>
          <div className="tw-flex tw-flex-col tw-w-full tw-gap-1 tw-min-w-0">
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
              <span
                className="tw-font-bold tw-text-sm tw-text-slate-800 hover:tw-text-theme tw-truncate tw-transition-colors tw-min-w-0 tw-flex-1"
                title={`${similarCompanyList[i]?.name}`}
              >
                {similarCompanyList[i]?.name}
              </span>
              <ChevronRight size={16} className="tw-text-slate-400 group-hover:tw-text-theme tw-transition-colors" />
            </div>

            <Tag className="tw-bg-slate-150 tw-text-slate-500 tw-rounded-md tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-border-none tw-w-fit tw-m-0">
              {similarCompanyList[i]?.industry?.name}
            </Tag>
            <div className="tw-text-[11px] tw-text-slate-400 tw-flex tw-items-center tw-gap-1.5 tw-mt-0.5">
              <Users size={12} className="tw-text-slate-400" />
              <span>{similarCompanyList[i]?.range?.name}</span>
            </div>
          </div>
        </div>,
      );
    }
    return similarList;
  };

  return (
    <>
      {renderSimilarCompanyList()?.length ? (
        <div className="tw-bg-white tw-border tw-border-solid tw-border-slate-200/80 tw-rounded-2xl tw-shadow-sm tw-flex tw-flex-col tw-shrink-0">
          <h3 className="tw-py-4 tw-px-5 tw-text-sm tw-font-bold tw-text-slate-800 tw-border-b tw-border-t-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-slate-100 tw-flex tw-items-center tw-gap-2.5 tw-shrink-0 tw-m-0">
            <Building2 size={16} className="tw-text-slate-600" />
            <span>Similar Companies</span>
          </h3>
          <div className="similarblk max-h-58vh tw-flex tw-flex-col tw-gap-2 tw-p-4 tw-overflow-y-auto">
            {renderSimilarCompanyList()}
            <div>
              {similarCompanyList?.length > 5 && (
                <div
                  className="tw-text-xs tw-text-center tw-text-theme tw-cursor-pointer tw-font-semibold tw-mt-2 hover:tw-underline"
                  onClick={getMoreSimilarCount}
                >
                  {similarCount === 10 ? "Show Less" : "Show More"}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        ""
      )}

      <div className="tw-bg-white tw-border tw-border-solid tw-border-slate-200/80 tw-rounded-2xl tw-shadow-sm tw-p-5 tw-mt-5 tw-text-sm tw-flex tw-flex-col tw-items-center tw-justify-center">
        <span className="tw-font-bold tw-text-slate-700 tw-mb-3">Is this company data relevant to you?</span>
        <div className="tw-flex tw-gap-4">
          {checkCompanyStatus === 1 ? (
            <span className="tw-flex tw-items-center tw-justify-center tw-size-10 tw-rounded-xl tw-bg-indigo-50 tw-text-theme tw-cursor-pointer tw-shadow-sm">
              <ThumbsUp size={18} />
            </span>
          ) : (
            <span
              onClick={() => checkRelavantCompany(1, checkCompanyStatus)}
              className="tw-flex tw-items-center tw-justify-center tw-size-10 tw-rounded-xl tw-bg-slate-50 tw-text-slate-450 hover:tw-bg-indigo-50 hover:tw-text-theme tw-cursor-pointer tw-transition-all tw-border tw-border-solid tw-border-slate-200/60"
            >
              <ThumbsUp size={18} />
            </span>
          )}
          {checkCompanyStatus === 2 ? (
            <span className="tw-flex tw-items-center tw-justify-center tw-size-10 tw-rounded-xl tw-bg-rose-50 tw-text-[#f44336] tw-cursor-pointer tw-shadow-sm">
              <ThumbsDown size={18} />
            </span>
          ) : (
            <span
              onClick={() => checkRelavantCompany(0, checkCompanyStatus)}
              className="tw-flex tw-items-center tw-justify-center tw-size-10 tw-rounded-xl tw-bg-slate-50 tw-text-slate-450 hover:tw-bg-rose-50 hover:tw-text-[#f44336] tw-cursor-pointer tw-transition-all tw-border tw-border-solid tw-border-slate-200/60"
            >
              <ThumbsDown size={18} />
            </span>
          )}
        </div>
      </div>

      {isAuthenticated && (
        <div
          className="tw-font-bold tw-py-2.5 tw-px-4 tw-gap-2 tw-flex tw-items-center tw-justify-center tw-rounded-xl tw-cursor-pointer tw-text-sm tw-mt-4 tw-border tw-border-solid tw-border-slate-200 hover:tw-border-rose-300 hover:tw-bg-rose-50/10 hover:tw-text-rose-600 tw-text-slate-650 tw-transition-all tw-shadow-sm"
          onClick={handleErrorForm}
        >
          <AlertTriangle size={16} className="tw-text-amber-500" />
          Report An Error
        </div>
      )}

      {openErrorForm && (
        <Modal
          title={
            <div className="tw-flex tw-flex-col tw-gap-0.5">
              <span className="tw-text-xs tw-font-bold tw-text-theme tw-uppercase tw-tracking-wider">
                Report an Error
              </span>
              <span className="tw-text-base tw-font-bold tw-text-slate-800">
                Report Incorrect Information
              </span>
            </div>
          }
          width="480px"
          closable
          centered
          open={openErrorForm}
          onCancel={closeErrorForm}
          footer={[
            <Button
              key="cancel"
              onClick={closeErrorForm}
              className="tw-border-slate-200 hover:tw-border-slate-300 tw-text-slate-600 hover:tw-text-slate-800 tw-text-xs tw-font-bold tw-h-10 tw-rounded-xl tw-px-5"
            >
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              onClick={onSubmitForm}
              className="!tw-bg-theme hover:!tw-bg-theme/90 !tw-border-theme tw-text-white tw-text-xs tw-font-bold tw-h-10 tw-rounded-xl tw-px-6"
            >
              Submit
            </Button>,
          ]}
        >
          <div className="tw-mt-4 tw-flex tw-flex-col tw-gap-3.5 tw-font-inter">
            <p className="tw-text-xs tw-font-medium tw-text-slate-500 tw-leading-relaxed tw-mb-1">
              Please select the appropriate checkbox that you have found
              Incorrect and if you know the correct data please provide us in
              appropriate text box.
            </p>

            {/* Telephone */}
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
              <div className="tw-w-[140px] tw-shrink-0">
                <Checkbox name="telephone" onChange={enableField} className="tw-text-xs tw-font-bold tw-text-slate-700">
                  Telephone
                </Checkbox>
              </div>
              <div className="tw-flex-1">
                <Input
                  name="telephone"
                  value={errorForm?.telephone?.value}
                  placeholder="Telephone"
                  disabled={errorForm?.telephone?.disabled}
                  onChange={onInputChange}
                  className="tw-h-10 tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 focus:tw-border-theme disabled:tw-bg-slate-50/70 disabled:tw-text-slate-400 tw-px-3"
                />
              </div>
            </div>

            {/* Address */}
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
              <div className="tw-w-[140px] tw-shrink-0 tw-mt-2">
                <Checkbox name="address" onChange={enableField} className="tw-text-xs tw-font-bold tw-text-slate-700">
                  Address
                </Checkbox>
              </div>
              <div className="tw-flex-1">
                <TextArea
                  name="address"
                  value={errorForm?.address?.value}
                  rows={2}
                  maxLength={100}
                  disabled={errorForm?.address?.disabled}
                  onChange={onInputChange}
                  placeholder="Address"
                  className="tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 focus:tw-border-theme disabled:tw-bg-slate-50/70 disabled:tw-text-slate-400 tw-p-2.5"
                />
              </div>
            </div>

            {/* City */}
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
              <div className="tw-w-[140px] tw-shrink-0">
                <Checkbox name="city" onChange={enableField} className="tw-text-xs tw-font-bold tw-text-slate-700">
                  City
                </Checkbox>
              </div>
              <div className="tw-flex-1">
                <Input
                  name="city"
                  value={errorForm?.city?.value}
                  placeholder="City"
                  disabled={errorForm?.city?.disabled}
                  onChange={onInputChange}
                  className="tw-h-10 tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 focus:tw-border-theme disabled:tw-bg-slate-50/70 disabled:tw-text-slate-400 tw-px-3"
                />
              </div>
            </div>

            {/* Zip */}
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
              <div className="tw-w-[140px] tw-shrink-0">
                <Checkbox name="zip" onChange={enableField} className="tw-text-xs tw-font-bold tw-text-slate-700">
                  Zip/Pin code
                </Checkbox>
              </div>
              <div className="tw-flex-1">
                <Input
                  name="zip"
                  value={errorForm?.zip?.value}
                  placeholder="Zip/Pin code"
                  disabled={errorForm?.zip?.disabled}
                  onChange={onInputChange}
                  className="tw-h-10 tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 focus:tw-border-theme disabled:tw-bg-slate-50/70 disabled:tw-text-slate-400 tw-px-3"
                />
              </div>
            </div>

            {/* Employee */}
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
              <div className="tw-w-[140px] tw-shrink-0">
                <Checkbox name="employee" onChange={enableField} className="tw-text-xs tw-font-bold tw-text-slate-700">
                  No. of Employees
                </Checkbox>
              </div>
              <div className="tw-flex-1">
                <Input
                  name="employee"
                  value={errorForm?.employee?.value}
                  placeholder="No. of Employees"
                  disabled={errorForm?.employee?.disabled}
                  onChange={onInputChange}
                  className="tw-h-10 tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 focus:tw-border-theme disabled:tw-bg-slate-50/70 disabled:tw-text-slate-400 tw-px-3"
                />
              </div>
            </div>

            {/* Website */}
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
              <div className="tw-w-[140px] tw-shrink-0">
                <Checkbox name="website" onChange={enableField} className="tw-text-xs tw-font-bold tw-text-slate-700">
                  Website
                </Checkbox>
              </div>
              <div className="tw-flex-1">
                <Input
                  name="website"
                  value={errorForm?.website?.value}
                  placeholder="Website"
                  disabled={errorForm?.website?.disabled}
                  onChange={onInputChange}
                  className="tw-h-10 tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 focus:tw-border-theme disabled:tw-bg-slate-50/70 disabled:tw-text-slate-400 tw-px-3"
                />
              </div>
            </div>

            <Divider dashed={true} className="tw-my-1" />

            {/* Name */}
            <div>
              <Input
                name="name"
                status={errorForm?.name?.status}
                value={errorForm?.name?.value}
                placeholder="Your Name*"
                onChange={onInputChange}
                className="tw-h-10 tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 focus:tw-border-theme tw-px-3"
              />
            </div>

            {/* Email */}
            <div>
              <Input
                name="email"
                status={errorForm?.email?.status}
                value={errorForm?.email?.value}
                placeholder="Your Email ID*"
                onChange={onInputChange}
                className="tw-h-10 tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 focus:tw-border-theme tw-px-3"
              />
            </div>

            {/* Comment */}
            <div>
              <TextArea
                name="comment"
                value={errorForm?.comment?.value}
                rows={3}
                maxLength={100}
                placeholder="Comment if any"
                onChange={onInputChange}
                className="tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 focus:tw-border-theme tw-p-2.5"
              />
            </div>
          </div>
        </Modal>
      )}

      {showModal ? (
        <TrialModal
          openModal={showModal}
          closeModal={closeModal}
          redirectToSignup={redirectToSignup}
          redirect={true}
        />
      ) : (
        ""
      )}
    </>
  );
};

export default SimilarCompany;
